import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import Layout from '../components/Layout';
import { api, formatCurrency, API_BASE } from '../lib/api';

const Collections = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [months, setMonths] = useState(12);
  const [drilldown, setDrilldown] = useState(null); // {year, month, label}
  const [drilldownRows, setDrilldownRows] = useState([]);

  useEffect(() => { api.get('/companies').then(r => setCompanies(r.data)); }, []);

  useEffect(() => {
    const params = { months };
    if (companyId) params.company_id = companyId;
    api.get('/collections/monthly', { params }).then(r => setSummary(r.data));
  }, [companyId, months]);

  const openDrilldown = (bucket) => {
    const [y, m] = bucket.month.split('-').map(Number);
    setDrilldown({ year: y, month: m, label: bucket.label });
    const params = { year: y, month: m };
    if (companyId) params.company_id = companyId;
    api.get('/collections/payments', { params }).then(r => setDrilldownRows(r.data));
  };

  return (
    <Layout
      title="Collections"
      action={
        <button data-testid="export-collections-csv" onClick={() => {
          const q = new URLSearchParams({ months });
          if (companyId) q.set('company_id', companyId);
          window.open(`${API_BASE}/exports/collections.csv?${q.toString()}`, '_blank');
        }} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-4 py-2 font-heading text-sm flex items-center transition-colors">
          <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Export CSV
        </button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div data-testid="collections-kpi-collected" className="bg-surface border border-border p-6">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-3">Collected ({months}m)</p>
          <p className="font-numbers text-3xl font-light text-success tabular-nums">{formatCurrency(summary?.total_collected || 0)}</p>
        </div>
        <div data-testid="collections-kpi-expenses" className="bg-surface border border-border p-6">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-3">Expenses ({months}m)</p>
          <p className="font-numbers text-3xl font-light text-maroon tabular-nums">{formatCurrency(summary?.total_expenses || 0)}</p>
        </div>
        <div data-testid="collections-kpi-net" className="bg-surface border border-border p-6 border-l-2 border-l-maroon">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-3">Net ({months}m)</p>
          <p className="font-numbers text-3xl font-light text-text-primary tabular-nums">{formatCurrency(summary?.net || 0)}</p>
        </div>
        <div className="bg-surface border border-border p-6">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-3">Filters</p>
          <select data-testid="collections-company-filter" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="w-full mb-2 bg-transparent border border-border focus:border-maroon outline-none px-2 py-1.5 font-body text-sm">
            <option value="">All companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.full_name || c.name}</option>)}
          </select>
          <select data-testid="collections-months-filter" value={months} onChange={(e) => setMonths(Number(e.target.value))} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-2 py-1.5 font-body text-sm">
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
            <option value={24}>Last 24 months</option>
          </select>
        </div>
      </div>

      <div className="bg-surface border border-border p-6 mb-8">
        <h3 className="font-heading text-lg font-medium text-text-primary mb-4 flex items-center"><TrendingUp className="w-4 h-4 mr-2 text-maroon" strokeWidth={1.5} /> Monthly Collections</h3>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={summary?.buckets || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9e4dd" />
              <XAxis dataKey="label" stroke="#777" style={{ fontSize: 11 }} />
              <YAxis stroke="#777" style={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Bar dataKey="total" fill="#7B1B1B" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" fill="#cf9b76" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-surface border border-border p-6">
        <h3 className="font-heading text-lg font-medium text-text-primary mb-4">Month-wise Breakdown</h3>
        <table className="w-full">
          <thead>
            <tr>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Month</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Collected</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Expenses</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {summary?.buckets.map(b => (
              <tr key={b.month} data-testid={`collections-row-${b.month}`} onClick={() => openDrilldown(b)} className="border-b border-border hover:bg-cream cursor-pointer transition-colors">
                <td className="py-3 font-body text-sm text-text-primary">{b.label}</td>
                <td className="py-3 font-numbers text-sm text-success text-right tabular-nums">{formatCurrency(b.total)}</td>
                <td className="py-3 font-numbers text-sm text-maroon text-right tabular-nums">{formatCurrency(b.expenses)}</td>
                <td className="py-3 font-numbers text-sm text-text-primary text-right tabular-nums font-medium">{formatCurrency(b.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drilldown && (
        <div data-testid="collections-drilldown" className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-cream border border-border w-[min(900px,95vw)] max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <h2 className="font-heading text-lg font-medium">Payments — {drilldown.label}</h2>
              <button onClick={() => setDrilldown(null)} className="font-heading text-sm text-text-secondary hover:text-maroon">Close</button>
            </div>
            <div className="flex-1 overflow-auto p-5">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Date</th>
                    <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Invoice</th>
                    <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Client</th>
                    <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Bank</th>
                    <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Ref</th>
                    <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {drilldownRows.map(r => (
                    <tr key={r.id} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="py-3 font-body text-sm">{r.date}</td>
                      <td className="py-3 font-body text-sm">{r.invoice_no}</td>
                      <td className="py-3 font-body text-sm">{r.client_name}</td>
                      <td className="py-3 font-body text-sm text-text-secondary">{r.bank_label}</td>
                      <td className="py-3 font-body text-sm text-text-secondary">{r.reference || '—'}</td>
                      <td className="py-3 font-numbers text-sm text-success text-right tabular-nums">{formatCurrency(r.amount)}</td>
                    </tr>
                  ))}
                  {drilldownRows.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-text-secondary">No payments in this month</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Collections;
